import React, { Component, Fragment } from "react";
import {
  getSingleOrderApiRedux,
  getSingleProductRequestRedux,
} from "../../actions/index";
import { connect } from "react-redux";
import { SkyArt, ZigEdge, ThanksStrip, CardIcon } from "./invoiceParts";
import KidoLogo from "../../assets/images/brand/kido-logo.png";
import { withRouter } from "react-router-dom";
import { getSingleUser } from "../../firebase/firebase.utils";

// Renders a numeric amount with its unit suffix, or "-" when the value
// is missing/not a number (instead of an empty "Tk"/"Kg" or "NaN Tk").
const fmtAmt = (value, suffix = "") => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? `${Math.trunc(n)}${suffix}` : "-";
};

export class OnlyInvoieToPrint extends Component {
  state = {
    user: null,
    orderObj: null,
    productRequestObj: null,
    productRequest: null,
  };
  componentDidMount = async () => {
    const query = new URLSearchParams(this.props.location.search);
    const productRequest = query.get("productRequest");
    if (productRequest) {
      await this.props.getSingleProductRequestRedux(
        this.props.match.params.bookingId
      );
    } else {
      await this.props.getSingleOrderApiRedux(
        this.props.match.params.bookingId
      );
    }
    this.setState({
      productRequest,
    });
  };

  componentWillReceiveProps = async (nextProps) => {
    if (nextProps.orderObj && nextProps.orderObj.userId) {
      const user = await getSingleUser(nextProps.orderObj.userId);
      this.setState({
        user: user,
      });
    }
    if (nextProps.productRequestObj && nextProps.productRequestObj.userId) {
      const user = await getSingleUser(nextProps.productRequestObj.userId);
      this.setState({
        user: user,
      });
    }
  };

  componentWillUnmount = () => {};

  render() {
    const { orderObj, productRequestObj } = this.props;
    const { user } = this.state;

    let renderableOrder = null;
    if (this.state.productRequest) {
      renderableOrder = productRequestObj;
    } else {
      renderableOrder = orderObj;
    }

    // Bill-to address: prefer the address captured on the order itself
    // (delivery/shipping address at order time) over the customer's
    // current profile address book, which may have since changed.
    let addressBook = null;
    if (renderableOrder && renderableOrder.deliveryAddress) {
      const da = renderableOrder.deliveryAddress;
      addressBook = {
        name: da.name || da.fullName || "",
        address: da.address || "",
        district: da.district || "",
        division: da.division || "",
        thana: da.thana || "",
        mobileNo: da.mobileNo || "",
      };
    } else if (user && user.addressBook && user.addressBook.length > 0) {
      addressBook = user.addressBook.find((address) => address.defaultShipping);
    }

    // The "ordersApi" collection holds two overlapping field layouts —
    // older admin-created orders (subTotal/discountApplied/couponApplied)
    // and newer checkout-created pre-orders (actualTotal/couponAmount) —
    // so every base-order figure below is normalized across both rather
    // than assuming one shape (same approach as the storefront's own
    // PreOrderInvoice, which reads the same collection).
    // Some orders store these amounts as strings ("1200"), which would turn
    // the additions below into string concatenation — coerce to numbers.
    const subtotal =
      Number(renderableOrder && (renderableOrder.actualTotal ?? renderableOrder.subTotal)) || 0;
    const deliveryCharge = Number(renderableOrder && renderableOrder.deliveryCharge) || 0;
    const discountApplied = Number(renderableOrder && renderableOrder.discountApplied) || 0;
    const couponDiscount =
      Number(
        renderableOrder &&
          (renderableOrder.couponAmount ?? renderableOrder.couponApplied?.discount),
      ) || 0;
    const baseTotal = subtotal + deliveryCharge - discountApplied - couponDiscount;

    // Freight/customs charges only exist once an order has been processed
    // through the China-warehouse buy-and-ship-for-me pipeline — absent
    // entirely on a plain pre-order, so each defaults to 0 rather than NaN.
    const otherCost = parseInt(renderableOrder && renderableOrder.otherCost) || 0;
    const shippingChargeCustomer =
      parseInt(renderableOrder && renderableOrder.shippingChargeCustomer) || 0;
    // localShipping ("China Shipping (local)" / post fee) is a checkout-time
    // field every pre-order has, not a signal that this order went through
    // freight processing — informational only, excluded from hasFreightInfo
    // and from the payable total (matches the admin Payment Management
    // modal's Price Breakdown, which shows it the same way).
    const localShipping = parseInt(renderableOrder && renderableOrder.localShipping) || 0;
    const hasFreightInfo = !!(
      renderableOrder &&
      (renderableOrder.receivedWeight ||
        renderableOrder.customerRate ||
        shippingChargeCustomer ||
        otherCost)
    );
    const totalPayableAmountIncludingShipping =
      baseTotal + otherCost + shippingChargeCustomer;

    let totalPaid = 0;
    if (renderableOrder && renderableOrder.payments && renderableOrder.payments.length > 0) {
      renderableOrder.payments.forEach((payment) => {
        totalPaid += parseInt(payment.amount) || 0;
      });
    }
    const due = Math.max(0, totalPayableAmountIncludingShipping - totalPaid);

    const payLabel =
      totalPaid >= totalPayableAmountIncludingShipping && totalPayableAmountIncludingShipping > 0
        ? "Paid"
        : totalPaid > 0
          ? "Partially Paid"
          : "Payment Due";
    const payClass =
      totalPaid >= totalPayableAmountIncludingShipping && totalPayableAmountIncludingShipping > 0
        ? "kdv-status--paid"
        : totalPaid > 0
          ? "kdv-status--partial"
          : "kdv-status--due";

    const isRequest = !!this.state.productRequest;
    const customerName =
      (addressBook && addressBook.name) || (user && user.displayName) || "";
    const firstName = customerName.trim().split(/\s+/)[0];
    const lines = isRequest
      ? []
      : ((orderObj && orderObj.items) || []).flatMap((item) =>
          (item.skus || []).map((sku, index) => ({ item, sku, index }))
        );
    const itemCount = isRequest
      ? productRequestObj
        ? parseInt(productRequestObj.productQuantity) || 1
        : 0
      : lines.reduce((s, l) => s + (parseInt(l.sku.totalQuantity) || 0), 0);

    return (
      <div className="kdv-page kdv-page--admin">
        <div className="kdv-doc">
          {payLabel === "Paid" && <div className="kdv-paid-stamp">Paid</div>}

          <div className={`kdv-sky${isRequest ? "" : " kdv-sky--po"}`}>
            <SkyArt preorder={!isRequest} />
            <div className="kdv-head">
              <div className="kdv-plaque">
                <img src={KidoLogo} alt="KIDO" className="kdv-logo" />
                {!isRequest && (
                  <span className="kdv-po-tag">
                    <CardIcon icon="plane" color="indigo" /> Pre-order
                  </span>
                )}
              </div>
              <div className="kdv-head-right">
                <div className="kdv-title">Invoice</div>
                <div>
                  <span className="kdv-number">#KD-{this.props.match.params.bookingId}</span>
                  <span className={`kdv-status ${payClass}`}>{payLabel}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="kdv-body">
            <div className="kdv-hello">
              <div>
                Hi {firstName || "there"}!
                <small>
                  {isRequest
                    ? "Here's the invoice for your product request."
                    : "Your pre-order is flying over to you."}
                </small>
              </div>
            </div>

            <div className="kdv-cards">
              <div className="kdv-card">
                <div className="kdv-card-label">
                  <CardIcon icon="balloon" color="pink" /> Deliver to
                </div>
                <div className="kdv-card-name">{customerName || "-"}</div>
                <div className="kdv-card-line">
                  {(addressBook && addressBook.mobileNo) || "-"}
                </div>
                <div className="kdv-card-line">
                  {(addressBook && addressBook.address) || "-"}
                </div>
                <div className="kdv-card-line">
                  {addressBook && addressBook.district ? `${addressBook.district}, ` : ""}
                  {(addressBook && addressBook.division) || ""}
                </div>
              </div>
              <div className="kdv-card kdv-card--butter">
                <div className="kdv-card-label">
                  <CardIcon icon="star" color="sun" /> Invoice details
                </div>
                <div className="kdv-kv">
                  <span>Submitted on</span>
                  <strong>
                    {isRequest
                      ? productRequestObj && productRequestObj.date
                      : orderObj && orderObj.orderedDate}
                  </strong>
                </div>
                <div className="kdv-kv">
                  <span>Invoice no</span>
                  <strong>#KD-{this.props.match.params.bookingId}</strong>
                </div>
                <div className="kdv-kv">
                  <span>Order ID</span>
                  <strong>{this.props.match.params.bookingId}</strong>
                </div>
                {renderableOrder && renderableOrder.shippingMark && (
                  <div className="kdv-kv">
                    <span>Shipping mark</span>
                    <strong>{renderableOrder.shippingMark}</strong>
                  </div>
                )}
                {renderableOrder && renderableOrder.ctnNo && (
                  <div className="kdv-kv">
                    <span>Carton no</span>
                    <strong>{renderableOrder.ctnNo.toString().replace("CB", "AR")}</strong>
                  </div>
                )}
                {renderableOrder && renderableOrder.trackingNo && (
                  <div className="kdv-kv">
                    <span>Tracking no</span>
                    <strong>{renderableOrder.trackingNo}</strong>
                  </div>
                )}
              </div>
            </div>

            <div className="kdv-items-head">
              <h2 className="kdv-items-title">Your goodies</h2>
              <span className="kdv-items-count">{itemCount}</span>
            </div>
            <div className="kdv-cols" aria-hidden="true">
              <span>Product</span>
              <span>Qty</span>
              <span>Unit price</span>
              <span>Total</span>
            </div>
            <div className="kdv-list">
              {isRequest
                ? productRequestObj && (
                    <div className="kdv-item">
                      <div className="kdv-product">
                        <div>
                          <div className="kdv-product-name">
                            {productRequestObj.store} - {productRequestObj.productType}
                          </div>
                        </div>
                      </div>
                      <span className="kdv-qty">×{fmtAmt(productRequestObj.productQuantity)}</span>
                      <div className="kdv-num">৳{fmtAmt(productRequestObj.unitPrice)}</div>
                      <div className="kdv-num kdv-line-total">
                        ৳
                        {fmtAmt(
                          parseFloat(productRequestObj.unitPrice) *
                            productRequestObj.productQuantity
                        )}
                      </div>
                    </div>
                  )
                : lines.map(({ item, sku, index }) => {
                    const img = sku.image || item.picture;
                    const variants = [sku.color, sku.size].filter(Boolean);
                    return (
                      <div key={`${item.id}-${index}`} className="kdv-item">
                        <div className="kdv-product">
                          {img && (
                            <span className="kdv-thumb">
                              <img src={img} alt={item.name || "product"} />
                            </span>
                          )}
                          <div>
                            <div className="kdv-product-name">
                              {(item.name || "").slice(0, 60)}
                            </div>
                            {variants.length > 0 && (
                              <div className="kdv-chips">
                                {variants.map((v, vi) => (
                                  <span className="kdv-chip" key={vi}>{v}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <span className="kdv-qty">×{fmtAmt(sku.totalQuantity)}</span>
                        <div className="kdv-num">৳{fmtAmt(sku.price)}</div>
                        <div className="kdv-num kdv-line-total">
                          ৳{fmtAmt(parseFloat(sku.price) * sku.totalQuantity)}
                        </div>
                      </div>
                    );
                  })}
            </div>

            {hasFreightInfo && (
              <div className="kdv-freight">
                <div>
                  <span>Total weight:</span>
                  <strong>{fmtAmt(renderableOrder && renderableOrder.receivedWeight, "Kg")}</strong>
                </div>
                <div>
                  <span>Customs &amp; shipping rate:</span>
                  <strong>{fmtAmt(renderableOrder && renderableOrder.customerRate, "Tk/Kg")}</strong>
                </div>
                <div>
                  <span>Total shipping charge:</span>
                  <strong>
                    {fmtAmt(renderableOrder && renderableOrder.shippingChargeCustomer, "Tk")}
                  </strong>
                </div>
                {renderableOrder && renderableOrder.refund ? (
                  <div>
                    <span>Refunded amount:</span>
                    <strong>{fmtAmt(renderableOrder.refund, "Tk")}</strong>
                  </div>
                ) : null}
              </div>
            )}

            {renderableOrder &&
              renderableOrder.payments &&
              renderableOrder.payments.length > 0 && (
                <>
                  <div className="kdv-payments-title">Previous payments</div>
                  <table className="kdv-payments">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Method</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderableOrder.payments.map((payment, pi) => (
                        <tr key={pi}>
                          <td>{payment.date}</td>
                          <td>{payment.method}</td>
                          <td>৳{payment.amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

            <div className="kdv-bottom">
              <div className="kdv-receipt-wrap">
                <div className="kdv-receipt">
                  <div className="kdv-trow">
                    <span>Subtotal</span>
                    <span>৳{subtotal}</span>
                  </div>
                  <div className="kdv-trow">
                    <span>Delivery</span>
                    <span>{deliveryCharge === 0 ? "Free" : `+৳${deliveryCharge}`}</span>
                  </div>
                  {discountApplied > 0 && (
                    <div className="kdv-trow kdv-trow--minus">
                      <span>Discount</span>
                      <span>-৳{discountApplied}</span>
                    </div>
                  )}
                  {couponDiscount > 0 && (
                    <div className="kdv-trow kdv-trow--minus">
                      <span>Coupon</span>
                      <span>-৳{couponDiscount}</span>
                    </div>
                  )}
                  {hasFreightInfo && (
                    <>
                      {otherCost > 0 && (
                        <div className="kdv-trow">
                          <span>Repackaging charge</span>
                          <span>+৳{otherCost}</span>
                        </div>
                      )}
                      {localShipping > 0 && (
                        <div className="kdv-trow">
                          <span>Local shipping (CN)</span>
                          <span>+৳{localShipping}</span>
                        </div>
                      )}
                      {shippingChargeCustomer > 0 && (
                        <div className="kdv-trow">
                          <span>Shipping charge (CN to BD)</span>
                          <span>+৳{shippingChargeCustomer}</span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="kdv-trow kdv-trow--total">
                    <span>Total</span>
                    <span>৳{totalPayableAmountIncludingShipping}</span>
                  </div>
                  <div className="kdv-trow kdv-trow--good">
                    <span>Paid</span>
                    <span>৳{totalPaid}</span>
                  </div>
                  {due > 0 && (
                    <div className="kdv-trow kdv-trow--minus">
                      <span>Due</span>
                      <span>৳{due}</span>
                    </div>
                  )}
                </div>
                <ZigEdge />
              </div>
            </div>

            <ThanksStrip
              note={
                hasFreightInfo
                  ? "Freight, approximate customs duty and a 5% service charge are included."
                  : undefined
              }
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    orderObj: state.orders.orderObj,
    productRequestObj: state.productRequests.productRequestObj,
    users: [],
    admin: null,
    user: null,
    currency: state.users.currency,
  };
};
export default withRouter(
  connect(mapStateToProps, {
    getSingleOrderApiRedux,
    getSingleProductRequestRedux,
  })(OnlyInvoieToPrint)
);
